import { Button } from "@/subframe/components/Button";
import { Tooltip } from "@/subframe/components/Tooltip";
import * as SubframeCore from "@subframe/core";
import { Table } from "@/subframe/components/Table";
import { IconButton } from "@/subframe/components/IconButton";
import { TextFieldUnstyled } from "@/subframe/components/TextFieldUnstyled";
import { ActionCard } from "@/subframe/components/ActionCard";
import { IconWithBackground } from "@/subframe/components/IconWithBackground";
import { AppDispatch, RootState } from "../redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  addKeywordToLabel,
  Label,
  removeKeywordFromLabel,
  logout,
} from "../redux/reducers/authSlice";
import { useEffect, useState } from "react";
import axios from "axios";
import { Switch } from "@/subframe/components/Switch";
import { addEsChatToConfig } from "../Utils/apiUtils";

import { setBrowserNotification } from "../redux/reducers/messengerSlice";
import { NotificationManager } from "../Utils/notificationHandler";

interface SettingsProps {
  isSettingOpen: boolean;
  setIsSettingOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLabelCreationOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLabelDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLabelId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

function Settings({
  isSettingOpen,
  setIsLabelCreationOpen,
  setIsSettingOpen,
  setIsLabelDeleteOpen,
  setLabelId,
}: SettingsProps) {
  const { labels } = useSelector((state: RootState) => state.auth);
  const { isBrowserNotificationOn } = useSelector(
    (state: RootState) => state.messenger
  );
  const dispatch = useDispatch<AppDispatch>();
  const [newKeyword, setNewKeyword] = useState("");
  const [isAddingKeyword, setIsAddingKeyword] = useState<number | null>(null);
  const [popupMessage, setPopupMessage] = useState<string | null>(null);
  const [popupType, setPopupType] = useState<"success" | "error" | null>(null);
  const [labelItem, setLabelItem] = useState<Label>({} as Label);


  useEffect(() => {
    if (isAddingKeyword !== null) {
      const selectedLabel = labels.find(
        (label) => label.id === isAddingKeyword
      );
      if (selectedLabel) setLabelItem(selectedLabel);
    }
  }, [isAddingKeyword, labels]);

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;

    try {
      const config = addEsChatToConfig();
      
      const response = await axios.patch(
        `https://api.insightssystem.com/api:bVXsw_FD/web/label_management/${labelItem.id}`,
        {
          id: labelItem.id,
          label: labelItem.label,
          created_at: labelItem.created_at,
          keywords: [...labelItem.keywords, newKeyword],
        },
        config
      );
      dispatch(
        addKeywordToLabel({ labelId: response.data.id, newKeyword: newKeyword })
      );
      setPopupMessage("Keyword added successfully!");
      setPopupType("success");
      setNewKeyword("");
      setIsAddingKeyword(null);

      setTimeout(() => {
        setPopupMessage(null);
        setPopupType(null);
      }, 3000);
    } catch (error) {
      setPopupMessage("Failed to add keyword.");
      setPopupType("error");

      setTimeout(() => {
        setPopupMessage(null);
        setPopupType(null);
      }, 3000);
    }
  };

  const handleRemoveKeyword = async (
    labelId: number,
    keywordToRemove: string
  ) => {
    try {
      const selectedLabel = labels.find((label) => label.id === labelId);
      if (!selectedLabel) return;

      const updatedKeywords = selectedLabel.keywords.filter(
        (keyword) => keyword !== keywordToRemove
      );

      const config = addEsChatToConfig();
      
      await axios.patch(
        `https://api.insightssystem.com/api:bVXsw_FD/web/label_management/${labelId}`,
        {
          id: selectedLabel.id,
          label: selectedLabel.label,
          created_at: selectedLabel.created_at,
          keywords: updatedKeywords,
        },
        config
      );

      dispatch(
        removeKeywordFromLabel({
          labelId: labelId,
          removedKeyword: keywordToRemove,
        })
      );

      setPopupMessage("Keyword removed successfully!");
      setPopupType("success");

      setTimeout(() => {
        setPopupMessage(null);
        setPopupType(null);
      }, 3000);
    } catch (error) {
      setPopupMessage("Failed to remove keyword.");
      setPopupType("error");

      setTimeout(() => {
        setPopupMessage(null);
        setPopupType(null);
      }, 3000);
    }
  };

  const handleBrowserNotificationToggle = async () => {
    const newState = !isBrowserNotificationOn;

    if (newState) {
      // Trying to enable notifications
      const success = await NotificationManager.enableNotifications();
      if (success) {
        dispatch(setBrowserNotification(true));
      } else {
        // Handle permission denied case
        // You might want to show a message to the user
        alert("Notification permission denied");
      }
    } else {
      // Turning off notifications
      NotificationManager.disableNotifications();
      dispatch(setBrowserNotification(false));
    }
  };

  const handleLogout = () => {
    // Remove all cookies with multiple approaches
    const cookies = document.cookie.split(";");
    
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // Try multiple combinations to ensure removal
      const removalAttempts = [
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.notifyer-system.com`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=notifyer-system.com`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.notifyer-systems.com`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=notifyer-systems.com`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure;sameSite=strict`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.notifyer-system.com;secure;sameSite=strict`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=notifyer-system.com;secure;sameSite=strict`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.notifyer-systems.com;secure;sameSite=strict`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=notifyer-systems.com;secure;sameSite=strict`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.notifyer-system.com;secure`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=notifyer-system.com;secure`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.notifyer-systems.com;secure`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=notifyer-systems.com;secure`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;sameSite=lax`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.notifyer-system.com;sameSite=lax`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=notifyer-system.com;sameSite=lax`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.notifyer-systems.com;sameSite=lax`,
        `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=notifyer-systems.com;sameSite=lax`
      ];
      
      removalAttempts.forEach(attempt => {
        document.cookie = attempt;
      });
    }
    
    // Clear local storage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      // Storage clear failed
    }
    
    // Dispatch logout action to clear Redux state
    dispatch(logout());
    
    // Force page reload to ensure cookies are gone
    setTimeout(() => {
      window.location.href = '/sign-in';
    }, 100);
  };

  return (
    <div className="bg-white">
      {popupMessage && popupType && (
        <div
          className={`fixed top-1 left-1/2 transform -translate-x-1/2 p-4 rounded-md text-white 
          ${popupType === "success" ? "bg-green-500" : "bg-red-500"}`}
        >
          {popupMessage}
        </div>
      )}
      <div className="flex w-full flex-col items-center gap-2 px-4 pt-4 pb-2">
        <div className="flex w-full items-center gap-1">
          <IconButton
            variant="brand-tertiary"
            size="large"
            icon="FeatherChevronLeft"
            onClick={() => setIsSettingOpen(!isSettingOpen)}
          />
          <span className="grow shrink-0 basis-0 text-heading-2 font-heading-2 text-default-font">
            Settings
          </span>
        </div>
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
        <div className="flex w-full items-center gap-2 py-1">
          <div className="flex grow shrink-0 basis-0 items-center gap-4">
            <div className="flex grow shrink-0 basis-0 items-center gap-2">
              <IconWithBackground size="small" icon="FeatherBell" />
              <span className="grow shrink-0 basis-0 text-heading-3 font-heading-3 text-default-font">
                Notification
              </span>
            </div>
          </div>
          <Switch
            checked={isBrowserNotificationOn}
            onCheckedChange={handleBrowserNotificationToggle}
          />
        </div>

        <div className="flex w-full items-center gap-2 py-1">
          <div className="flex grow shrink-0 basis-0 items-center gap-4">
            <div className="flex grow shrink-0 basis-0 items-center gap-2">
              <IconWithBackground size="small" icon="FeatherBadgeInfo" />
              <span className="grow shrink-0 basis-0 text-heading-3 font-heading-3 text-default-font">
                Download Chat
              </span>
            </div>
          </div>
          <IconButton
            variant="brand-secondary"
            icon="FeatherDownload"
            onClick={async () => {
              const config = addEsChatToConfig();
              
              const response = await fetch('https://api.insightssystem.com/api:bVXsw_FD/recipient_download_csv', {
                headers: config.headers as Record<string, string>,
              });
              
              if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'recipients.csv';
                a.click();
                window.URL.revokeObjectURL(url);
              }
            }}
          />
        </div>

        <div className="flex w-full items-center gap-2 py-1">
          <div className="flex grow shrink-0 basis-0 items-center gap-4">
            <div className="flex grow shrink-0 basis-0 items-center gap-2">
              <IconWithBackground size="small" icon="FeatherLogOut" />
              <span className="grow shrink-0 basis-0 text-heading-3 font-heading-3 text-default-font">
                Logout
              </span>
            </div>
          </div>
          <Button
            variant="destructive-primary"
            size="small"
            onClick={handleLogout}
          >
            Logout
          </Button>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-100" />
        <div className="flex flex-col items-start gap-1">
          <div className="flex w-full items-center gap-2">
            <IconWithBackground size="small" icon="FeatherTag" />
            <span className="grow shrink-0 basis-0 text-heading-3 font-heading-3 text-default-font">
              Label Management
            </span>
          </div>
          <span className="text-body font-body text-subtext-color">
            Label conversations based on keywords in messages to organize and
            navigate chats more efficiently
          </span>
        </div>
      </div>

      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 overflow-hidden px-4 py-4">
        <div className="flex flex-col items-start gap-1">
          <div className="flex w-full items-center gap-2 px-2 py-1 min-w-[350px] ">
            <div className="flex grow shrink-0 basis-0 items-center gap-4">
              <div className="flex grow shrink-0 basis-0 items-center gap-2">
                <span className="grow shrink-0 basis-0 text-heading-3 font-heading-3 text-default-font">
                  Add Labels
                </span>
              </div>
            </div>
            <Button
              className="h-6 w-auto flex-none"
              icon="FeatherPlus"
              onClick={() => setIsLabelCreationOpen(true)}
            >
              Create Label
            </Button>
          </div>
          <div className="conveythis-no-translate flex flex-col min-w-[350px] items-start overflow-hidden rounded-md border border-solid border-neutral-border bg-default-background shadow-sm">
            <Table
              className="h-auto w-auto flex-none min-w-[350px]"
              header={
                <Table.HeaderRow>
                  <SubframeCore.Tooltip.Provider>
                    <SubframeCore.Tooltip.Root>
                      <SubframeCore.Tooltip.Trigger asChild={true}>
                        <Table.HeaderCell icon="FeatherInfo">
                          Label
                        </Table.HeaderCell>
                      </SubframeCore.Tooltip.Trigger>
                      <SubframeCore.Tooltip.Portal>
                        <SubframeCore.Tooltip.Content
                          side="top"
                          align="start"
                          sideOffset={0}
                          asChild={true}
                        >
                          <Tooltip>Labels</Tooltip>
                        </SubframeCore.Tooltip.Content>
                      </SubframeCore.Tooltip.Portal>
                    </SubframeCore.Tooltip.Root>
                  </SubframeCore.Tooltip.Provider>
                  <SubframeCore.Tooltip.Provider>
                    <SubframeCore.Tooltip.Root>
                      <SubframeCore.Tooltip.Trigger asChild={true}>
                        <Table.HeaderCell icon="FeatherInfo">
                          Keyword
                        </Table.HeaderCell>
                      </SubframeCore.Tooltip.Trigger>
                      <SubframeCore.Tooltip.Portal>
                        <SubframeCore.Tooltip.Content
                          side="top"
                          align="start"
                          sideOffset={0}
                          asChild={true}
                        >
                          <Tooltip>Keyword</Tooltip>
                        </SubframeCore.Tooltip.Content>
                      </SubframeCore.Tooltip.Portal>
                    </SubframeCore.Tooltip.Root>
                  </SubframeCore.Tooltip.Provider>
                  <Table.HeaderCell />
                </Table.HeaderRow>
              }
            >
              {labels && labels.length > 0 ? (
                labels.map((labelItem: Label) => (
                  <Table.Row key={labelItem.id}>
                    <Table.Cell className="h-12 w-auto flex-none">
                      <span className="text-body-bold font-body-bold text-default-font">
                        {labelItem.label}
                      </span>
                    </Table.Cell>

                    <Table.Cell className="h-auto grow shrink-0 basis-0 self-stretch">
                      <div className="flex grow shrink-0 basis-0 items-end gap-1">
                        <div
                          className={`${
                            labelItem.keywords.length > 0
                              ? "flex flex-wrap items-start gap-1 px-1 py-1"
                              : ""
                          }`}
                        >
                          {labelItem.keywords.length > 0 &&
                            labelItem.keywords.map((keyword, index) => (
                              <div
                                key={index}
                                className="flex grow shrink-0 basis-0 items-center gap-0.5 rounded-md border border-solid border-brand-100 bg-brand-100 px-1 py-px"
                              >
                                <span className="whitespace-nowrap text-caption font-caption text-brand-800">
                                  {keyword}
                                </span>
                                <IconButton
                                  className="h-4 w-4 flex-none"
                                  size="small"
                                  icon="FeatherX"
                                  onClick={() =>
                                    handleRemoveKeyword(labelItem.id, keyword)
                                  }
                                />
                              </div>
                            ))}
                          {isAddingKeyword === labelItem.id && (
                            <div className="flex w-full items-center gap-2 rounded-md border border-solid border-neutral-border pl-2">
                              <TextFieldUnstyled>
                                <TextFieldUnstyled.Input
                                  placeholder="Enter Keyword"
                                  value={newKeyword}
                                  onChange={(e) =>
                                    setNewKeyword(e.target.value)
                                  }
                                  onKeyDown={(e) =>
                                    e.key === "Enter" && handleAddKeyword()
                                  }
                                />
                              </TextFieldUnstyled>
                            </div>
                          )}
                        </div>
                        {/* Toggle button to enable/disable input for the specific label */}
                        <IconButton
                          className="h-7 w-5 flex-none"
                          variant="brand-tertiary"
                          size="small"
                          icon={
                            isAddingKeyword === labelItem.id
                              ? "FeatherX"
                              : "FeatherPlus"
                          }
                          onClick={() =>
                            setIsAddingKeyword(
                              isAddingKeyword === labelItem.id
                                ? null
                                : labelItem.id
                            )
                          }
                        />
                        {/* Check button for confirming keyword entry */}
                        {isAddingKeyword === labelItem.id && (
                          <IconButton
                            className="h-7 w-5 flex-none"
                            variant="inverse"
                            size="small"
                            icon="FeatherCheck"
                            onClick={() => {
                              if (!labelItem.id) return;
                              handleAddKeyword();
                            }}
                          />
                        )}
                      </div>
                    </Table.Cell>

                    {/* Delete button */}
                    <Table.Cell>
                      <div className="flex grow shrink-0 basis-0 items-center justify-end">
                        <IconButton
                          icon="FeatherTrash"
                          onClick={() => {
                            setLabelId(labelItem.id);
                            setIsLabelDeleteOpen(true);
                          }}
                        />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell>
                    <h3 className="flex  items-center justify-center text-center">
                      No Labels available
                    </h3>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table>
          </div>
        </div>
        <div className="hidden w-full grow shrink-0 basis-0 flex-col items-center justify-end gap-4">
          <ActionCard
            className="h-auto w-full flex-none"
            icon="FeatherSettings2"
            title="Chat Setting"
            desc="Usability tests, surveys, card sort, and more"
          />
        </div>
      </div>
    </div>
  );
}

export default Settings;
